import {serviceAttemptOutcomeLabels} from './serviceAttemptOutcomes';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {ApiError, assignCompanyManagedOrder, AssignmentOptions, CompanyManagedAssignment, dispatchCompanyManagedAssignment, loadCompanyManagedAssignmentOptions, loadCompanyManagedAssignments, loadServerWorkQueue, recordServiceAttempt, submitAttemptEvidence} from './api';

type State = 'loading' | 'ready' | 'pending' | 'success' | 'validation' | 'denied' | 'conflict' | 'error';
const message: Record<State, string> = {
  loading: 'Loading authorized orders and eligible servers…', ready: 'Choose an authorized order and eligible process server.',
  pending: 'Saving assignment change…', success: 'Assignment change completed.',
  validation: 'Choose a valid authorized order and eligible server.',
  denied: 'The assignment action is not available. Reload current work.',
  conflict: 'The assignment changed. Reload current work before taking another action.',
  error: 'The assignment result could not be confirmed. Reload current work before taking another action.',
};
const loadAssignmentChoices = () => Promise.all([loadCompanyManagedAssignments(), loadCompanyManagedAssignmentOptions()]);

export function CompanyManagedAssignmentAdmin({refreshKey = 0}: {refreshKey?: number} = {}) {
  const [items, setItems] = useState<CompanyManagedAssignment[]>([]);
  const [options, setOptions] = useState<AssignmentOptions>({authorizations: [], servers: []});
  const [state, setState] = useState<State>('loading');
  const [status, setStatus] = useState('');
  const [authorizationId, setAuthorizationId] = useState('');
  const [serverId, setServerId] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const busy = useRef(false);
  const generation = useRef(0);

  function clearChoices() {
    setItems([]); setOptions({authorizations: [], servers: []});
    setAuthorizationId(''); setServerId('');
  }
  useEffect(() => {
    let active = true;
    const request = ++generation.current;
    clearChoices(); setState('loading'); setStatus('');
    loadAssignmentChoices().then(([next, choices]) => {
      if (!active || request !== generation.current) return;
      setItems(next); setOptions(choices); setState('ready');
    }).catch(error => {
      if (active && request === generation.current) {
        setState(error instanceof ApiError && error.status === 403 ? 'denied' : 'error');
        setStatus('Current assignments and eligible servers could not be loaded. Reload current work.');
      }
    });
    return () => {active = false;};
  }, [refreshKey, reloadKey]);

  const disabled = !['ready', 'success', 'validation'].includes(state);
  async function change(action: () => Promise<CompanyManagedAssignment>) {
    if (busy.current || disabled) return;
    busy.current = true;
    const request = generation.current;
    setState('pending'); setStatus('');
    let saved = false;
    try {
      await action(); saved = true;
      const [next, choices] = await loadAssignmentChoices();
      if (request !== generation.current) {setReloadKey(key => key + 1); return;}
      setItems(next); setOptions(choices); setAuthorizationId(''); setServerId(''); setState('success');
    } catch (error) {
      ++generation.current;
      clearChoices();
      setState(error instanceof ApiError ? (error.status === 403 ? 'denied' : error.status === 409 ? 'conflict' : 'error') : 'error');
      if (saved) setStatus('The assignment change was saved, but current work could not be loaded. Reload current work to see the saved change.');
    } finally {busy.current = false;}
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current || disabled) return;
    const authorization = options.authorizations.find(x => x.authorization_id === authorizationId);
    const server = options.servers.find(x => x.server_identity_id === serverId);
    if (!authorization || !server) {setState('validation'); return;}
    void change(() => assignCompanyManagedOrder(authorization.authorization_id, authorization.authorization_version, server.server_identity_id, 'company_dispatch', crypto.randomUUID()));
  }
  return <section className="panel" aria-labelledby="assignment-heading">
    <h2 id="assignment-heading">Company-managed assignments</h2>
    <p role="status" aria-live="polite">{status || message[state]}</p>
    <button type="button" disabled={state === 'loading' || state === 'pending'} onClick={() => setReloadKey(key => key + 1)}>Reload current work</button>
    <form onSubmit={submit}>
      <label>Authorized order<select name="authorization" required disabled={disabled} value={authorizationId} onChange={event => setAuthorizationId(event.target.value)}>
        <option value="" disabled>Select an authorized order</option>
        {options.authorizations.map(x => <option key={x.authorization_id} value={x.authorization_id}>{x.service_type_code} due {x.deadline_date} — {x.city}, {x.admin_area}</option>)}
      </select></label>
      <label>Eligible process server<select name="server" required disabled={disabled} value={serverId} onChange={event => setServerId(event.target.value)}>
        <option value="" disabled>Select an eligible server</option>
        {options.servers.map(x => <option key={x.server_identity_id} value={x.server_identity_id}>{x.display_name}</option>)}
      </select></label>
      <button type="submit" disabled={disabled || options.authorizations.length === 0 || options.servers.length === 0}>Create assignment</button>
    </form>
    <ul>{items.map(item => <li key={item.assignment_id}>
      Order {item.order_id}: {item.state}. Attempts: {Number(item.attempt_count ?? 0)}. Evidence received: {Number(item.evidence_count ?? 0)}.
      {item.latest_outcome_code && <> Latest factual outcome: {item.latest_outcome_code}.</>}
      {item.state === 'assigned' && <button disabled={disabled} onClick={() => void change(() => dispatchCompanyManagedAssignment(item.assignment_id, item.version, crypto.randomUUID()))}>Dispatch to server</button>}
    </li>)}</ul>
  </section>;
}

export function CompanyManagedServerWorkQueue({onEvidenceSaved}: {onEvidenceSaved?: () => void} = {}) {
  const [items, setItems] = useState<CompanyManagedAssignment[] | null>(null);
  const [status, setStatus] = useState('Loading dispatched work…');
  const [pending, setPending] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const busy = useRef(true);
  useEffect(() => {
    let active = true;
    busy.current = true; setPending(true); setItems(null); setStatus('Loading dispatched work…');
    loadServerWorkQueue().then(next => {
      if (active) {setItems(next); setStatus(next.length ? 'Dispatched work loaded.' : 'No dispatched work.');}
    }).catch(() => {if (active) setStatus('Work queue is not available. Reload dispatched work.');})
      .finally(() => {if (active) {busy.current = false; setPending(false);}});
    return () => {active = false;};
  }, [reloadKey]);

  async function save(item: CompanyManagedAssignment, form: HTMLFormElement, action: () => Promise<unknown>, kind: 'Service attempt' | 'Private attempt evidence') {
    if (busy.current || items === null) return;
    busy.current = true; setPending(true); setPendingId(item.assignment_id); setStatus(`Saving ${kind.toLowerCase()}…`);
    let saved = false;
    try {
      await action(); saved = true; form.reset();
      if (kind === 'Private attempt evidence') onEvidenceSaved?.();
      setItems(await loadServerWorkQueue());
      setStatus(kind === 'Service attempt' ? 'Service attempt and factual outcome recorded.' : 'Private attempt evidence received and integrity-checked.');
    } catch (error) {
      if (!saved && error instanceof ApiError && error.status === 400) {
        setStatus(kind === 'Service attempt' ? 'Check the structured attempt fields and try again.' : 'Check the evidence type, size, hash, and capture time.');
      } else {
        setItems(null);
        if (saved) setStatus(`${kind} was saved, but the work queue could not be loaded. Reload dispatched work to see the saved result.`);
        else if (error instanceof ApiError && error.status === 403) setStatus('This work is not available. Reload dispatched work.');
        else if (error instanceof ApiError && error.status === 409) setStatus('The work changed. Reload dispatched work before taking another action.');
        else setStatus('The result could not be confirmed. Reload dispatched work and check the latest attempt and evidence before taking another action.');
      }
    } finally {busy.current = false; setPending(false); setPendingId(null);}
  }
  function submit(event: FormEvent<HTMLFormElement>, item: CompanyManagedAssignment) {
    event.preventDefault();
    if (busy.current || items === null) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const observed = new Date(String(data.get('observed_at') ?? ''));
    const outcome = String(data.get('outcome_code') ?? '');
    const note = String(data.get('factual_note') ?? '');
    if (!Number.isFinite(observed.getTime()) || !outcome) {setStatus('Complete the observed time and factual outcome.'); return;}
    void save(item, form, () => recordServiceAttempt(item.assignment_id, item.version, Number(item.attempt_count ?? 0) + 1, observed.toISOString(), outcome, note, crypto.randomUUID()), 'Service attempt');
  }
  function submitEvidence(event: FormEvent<HTMLFormElement>, item: CompanyManagedAssignment) {
    event.preventDefault();
    if (busy.current || items === null || !item.latest_attempt_id || item.latest_attempt_version == null) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = (form.elements.namedItem('evidence') as HTMLInputElement | null)?.files?.[0] ?? null;
    const captured = new Date(String(data.get('captured_at') ?? ''));
    if (file === null || file.size < 1 || file.size > 8192 || !Number.isFinite(captured.getTime())) {
      setStatus('Choose one supported private evidence file up to 8 KiB and its capture time.'); return;
    }
    const attemptId = item.latest_attempt_id;
    void save(item, form, () => submitAttemptEvidence(item.assignment_id, attemptId, Number(item.latest_attempt_version), file, captured.toISOString(), crypto.randomUUID()), 'Private attempt evidence');
  }
  return <section className="panel" aria-labelledby="work-heading">
    <h2 id="work-heading">My work queue</h2>
    <p role="status" aria-live="polite">{status}</p>
    <button type="button" disabled={pending} onClick={() => setReloadKey(key => key + 1)}>Reload dispatched work</button>
    <ul>{items?.map(x => <li key={x.assignment_id}>
      <strong>{x.service_type_code}</strong> due {x.deadline_date}
      <h4>Documents to serve</h4>{x.documents_to_serve?.length?<ol>{x.documents_to_serve.map((title,i)=><li key={i}>{title}</li>)}</ol>:<p>No titled packet recorded; confirm with the company.</p>}
      <address>{x.address_line_1}{x.address_line_2 && <>, {x.address_line_2}</>}<br/>{x.city}, {x.admin_area} {x.postal_code} {x.country_code}</address>
      <p>Recorded attempts: {Number(x.attempt_count ?? 0)}{x.latest_outcome_code && <>. Latest factual outcome: {x.latest_outcome_code}</>}. Evidence received: {Number(x.evidence_count ?? 0)}.</p>
      <form onSubmit={event => submit(event, x)}><fieldset disabled={pending}>
        <legend>Record structured service attempt</legend>
        <label>Observed at<input name="observed_at" type="datetime-local" required/></label>
        <label>Factual outcome<select name="outcome_code" required defaultValue="">
          <option value="" disabled>Select an observed outcome</option>
          {Object.entries(serviceAttemptOutcomeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Factual note<textarea name="factual_note" maxLength={500} aria-describedby={`note-help-${x.assignment_id}`}/></label>
        <small id={`note-help-${x.assignment_id}`}>Record only what was observed. Do not state legal conclusions.</small>
        <button type="submit">{pendingId === x.assignment_id ? 'Saving…' : 'Save attempt and outcome'}</button>
      </fieldset></form>
      {x.latest_attempt_id && x.latest_attempt_version != null && <form onSubmit={event => submitEvidence(event, x)}><fieldset disabled={pending}>
        <legend>Add private evidence to latest attempt</legend>
        <label>Evidence file<input name="evidence" type="file" required accept="image/jpeg,image/png,application/pdf,video/mp4" aria-describedby={`evidence-help-${x.assignment_id}`}/></label>
        <small id={`evidence-help-${x.assignment_id}`}>Private JPEG, PNG, PDF, or MP4 only; maximum 8 KiB. Customers receive status only.</small>
        <label>Captured at<input name="captured_at" type="datetime-local" required/></label>
        <button type="submit">{pendingId === x.assignment_id ? 'Saving…' : 'Save private evidence'}</button>
      </fieldset></form>}
    </li>)}</ul>
  </section>;
}
